import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Label } from '../ui/label';
import { Plus, Edit, Trash2, Building2, Loader2 } from 'lucide-react';
import { departmentApi, employeeApi } from '../../services/api';
import { Department } from '../../types/hr';
import { useToast } from '../../hooks/use-toast';

interface DepartmentFormData {
  name: string;
  managerId: string;
  parentId: string;
  color: string;
}

const DepartmentList: React.FC = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState<Department | null>(null);
  const [formData, setFormData] = useState<DepartmentFormData>({
    name: '',
    managerId: 'none',
    parentId: 'none',
    color: '#143A62'
  });

  // Fetch departments
  const { data: departments = [], isLoading: depsLoading, error: depsError } = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentApi.getAll(),
  });

  // Fetch employees for manager selection
  const { data: employees = [] } = useQuery({
    queryKey: ['employees'],
    queryFn: () => employeeApi.getAll({}),
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: departmentApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      setIsCreateOpen(false);
      setFormData({ name: '', managerId: 'none', parentId: 'none', color: '#143A62' });
      toast({ title: 'Department created successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error creating department', description: error.message, variant: 'destructive' });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => departmentApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      setIsEditOpen(false);
      setEditingDepartment(null);
      toast({ title: 'Department updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error updating department', description: error.message, variant: 'destructive' });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: departmentApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      toast({ title: 'Department deleted successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error deleting department', description: error.message, variant: 'destructive' });
    },
  });

  // Helper function to get full name
  const getFullName = (firstName: string, lastName?: string): string => {
    return lastName ? `${firstName} ${lastName}` : firstName;
  };

  // Get employee by ID
  const getEmployeeById = (id: string) => {
    return employees.find(emp => emp.id === id);
  };

  const colorOptions = [
    { value: '#143A62', label: 'Navy', color: '#143A62' },
    { value: '#3C6D78', label: 'Peacock Blue', color: '#3C6D78' },
    { value: '#EA612D', label: 'Orange', color: '#EA612D' },
    { value: '#86A09B', label: 'Sage', color: '#86A09B' },
    { value: '#B0BCD2', label: 'Lavender', color: '#B0BCD2' }
  ];

  const handleCreate = () => {
    createMutation.mutate({
      name: formData.name,
      manager_id: formData.managerId === 'none' ? null : formData.managerId,
      parent_id: formData.parentId === 'none' ? null : formData.parentId,
      color: formData.color,
    });
  };

  const handleEdit = () => {
    if (!editingDepartment) return;
    
    updateMutation.mutate({
      id: editingDepartment.id,
      data: {
        name: formData.name,
        manager_id: formData.managerId === 'none' ? null : formData.managerId,
        parent_id: formData.parentId === 'none' ? null : formData.parentId,
        color: formData.color,
      },
    });
  };

  const handleDelete = (departmentId: string) => {
    if (confirm('Are you sure you want to delete this department?')) {
      deleteMutation.mutate(departmentId);
    }
  };

  const openEditDialog = (department: Department) => {
    setEditingDepartment(department);
    setFormData({
      name: department.name,
      managerId: department.manager_id || 'none',
      parentId: department.parent_id || 'none',
      color: department.color || '#143A62'
    });
    setIsEditOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      managerId: 'none',
      parentId: 'none',
      color: '#143A62'
    });
  };

  const getEmployeeCount = (departmentId: string): number => {
    return employees.filter(emp => emp.department_id === departmentId).length;
  };

  const getParentDepartment = (parentId?: string): Department | null => {
    return parentId ? departments.find(dept => dept.id === parentId) || null : null;
  };

  const getManager = (managerId?: string) => {
    return managerId ? getEmployeeById(managerId) : null;
  };

  const availableParents = departments.filter(dept => {
    if (editingDepartment) {
      return dept.id !== editingDepartment.id;
    }
    return true;
  });

  // Loading state
  if (depsLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading departments...</span>
      </div>
    );
  }

  // Error state
  if (depsError) {
    return (
      <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-lg">
        <p className="font-semibold">Error loading departments</p>
        <p className="text-sm mt-1">{depsError.message || 'Please try again later'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Departments</h1>
          <p className="text-gray-600 mt-2">Manage organizational departments and their structure</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="flex items-center   bg-[var(--color-secondary)] hover:bg-[var(--color-secondary-hover)] space-x-2">
              <Plus className="h-4 w-4" />
              <span>Add Department</span>
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Department</DialogTitle>
              <DialogDescription>
                Add a new department to your organization structure.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="name" className="text-right">
                  Name
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="col-span-3"
                  placeholder="Department name"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="manager" className="text-right">
                  Manager
                </Label>
                <Select value={formData.managerId} onValueChange={(value) => setFormData({ ...formData, managerId: value })}>
                  <SelectTrigger className="col-span-3">
                    <SelectValue placeholder="Select manager" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Manager</SelectItem>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {getFullName(emp.first_name, emp.last_name)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="parent" className="text-right">
                  Parent Department
                </Label>
                <Select value={formData.parentId} onValueChange={(value) => setFormData({ ...formData, parentId: value })}>
                  <SelectTrigger className="col-span-3">
                    <SelectValue placeholder="Select parent department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Parent</SelectItem>
                    {availableParents.map(dept => (
                      <SelectItem key={dept.id} value={dept.id}>
                        {dept.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="color" className="text-right">
                  Color
                </Label>
                <Select value={formData.color} onValueChange={(value) => setFormData({ ...formData, color: value })}>
                  <SelectTrigger className="col-span-3">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {colorOptions.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex items-center space-x-2">
                          <div 
                            className="w-4 h-4 rounded-full" 
                            style={{ backgroundColor: option.color }}
                          />
                          <span>{option.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="soft" onClick={() => setIsCreateOpen(false)}>
                Cancel
              </Button>
               <Button className="flex items-center   bg-[var(--color-secondary)] hover:bg-[var(--color-secondary-hover)] space-x-2" onClick={handleCreate} disabled={!formData.name}>
                Create Department
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Departments Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Building2 className="h-5 w-5" />
            <span>All Departments</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Department</TableHead>
                <TableHead>Manager</TableHead>
                <TableHead>Parent Department</TableHead>
                <TableHead>Employees</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {departments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    No departments found. Add your first department to get started.
                  </TableCell>
                </TableRow>
              ) : (
                departments.map((department) => {
                  const manager = getManager(department.manager_id);
                  const parentDept = getParentDepartment(department.parent_id);
                  const employeeCount = getEmployeeCount(department.id);

                  return (
                    <TableRow key={department.id}>
                      <TableCell>
                        <div className="flex items-center space-x-3">
                          <div 
                            className="w-4 h-4 rounded-full" 
                            style={{ backgroundColor: department.color }}
                          />
                          <div>
                            <div className="font-medium text-gray-900">
                              {department.name}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {manager ? (
                          <div className="font-medium">
                            {getFullName(manager.first_name, manager.last_name)}
                          </div>
                        ) : (
                          <span className="text-gray-500">No Manager</span>
                        )}
                    </TableCell>
                    <TableCell>
                      {parentDept ? (
                        <Badge variant="outline">{parentDept.name}</Badge>
                      ) : (
                        <span className="text-gray-500">Root Department</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{employeeCount} employees</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Button 
                          variant="soft" 
                          size="sm"
                          onClick={() => openEditDialog(department)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="soft" 
                          size="sm"
                          onClick={() => handleDelete(department.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Department</DialogTitle>
            <DialogDescription>
              Update department information and structure.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-name" className="text-right">
                Name
              </Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-manager" className="text-right">
                Manager
              </Label>
              <Select value={formData.managerId} onValueChange={(value) => setFormData({ ...formData, managerId: value })}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select manager" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Manager</SelectItem>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {getFullName(emp.first_name, emp.last_name)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-parent" className="text-right">
                Parent Department
              </Label>
              <Select value={formData.parentId} onValueChange={(value) => setFormData({ ...formData, parentId: value })}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select parent department" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Parent</SelectItem>
                  {availableParents.map(dept => (
                    <SelectItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-color" className="text-right">
                Color
              </Label>
              <Select value={formData.color} onValueChange={(value) => setFormData({ ...formData, color: value })}>
                <SelectTrigger className="col-span-3">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {colorOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex items-center space-x-2">
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: option.color }}
                        />
                        <span>{option.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEdit} disabled={!formData.name}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DepartmentList;